import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';

/** One row of the Plans card: who is on this plan and what it bills monthly. */
export interface PlanBucket {
  slug: string;
  name: string;
  organizations: number;
  users: number;
  mrrCents: number;
}

export interface Stats {
  total: number;
  healthy: number;
  expiring: number;
  expired: number;
  workspaces: number;
  emptyWorkspaces: number;
  users: number;
  forwardingEnabled: number;
  forwardingDisabled: number;
  planBreakdown?: PlanBucket[];
}

// Catalog prices are whole dollars, but a custom-priced org can bill cents —
// show them rather than rounding $99.50 up to $100.
const USD = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 0,
  maximumFractionDigits: 2,
});

function usd(cents: number): string {
  return USD.format(cents / 100);
}

interface StatCardsProps {
  stats: Stats | null;
  isLoading: boolean;
}

export function StatCards({ stats, isLoading }: StatCardsProps) {
  if (isLoading || !stats) {
    return (
      <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
        {Array.from({ length: 4 }).map((_, i) => (
          <Card key={i}>
            <CardContent className="p-4">
              <Skeleton className="h-16 w-full" />
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  const plans = stats.planBreakdown ?? [];

  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-4">
      <Card>
        <CardContent className="p-4">
          <p className="text-xs font-semibold text-muted-foreground">
            Total Channels
          </p>
          <p className="text-2xl font-semibold">{stats.total}</p>
          <div className="mt-1 flex gap-2 text-xs">
            <span className="text-green-600">{stats.healthy} healthy</span>
            <span className="text-yellow-600">{stats.expiring} expiring</span>
            <span className="text-red-600">{stats.expired} expired</span>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-4">
          <p className="text-xs font-semibold text-muted-foreground">
            Workspaces
          </p>
          <p className="text-2xl font-semibold">{stats.workspaces}</p>
          <div className="mt-1 flex gap-2 text-xs">
            <span>
              {stats.workspaces - stats.emptyWorkspaces} active /{' '}
              {stats.emptyWorkspaces} empty
            </span>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-4">
          <p className="text-xs font-semibold text-muted-foreground">Users</p>
          <p className="text-2xl font-semibold">{stats.users}</p>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-4">
          <p className="text-xs font-semibold text-muted-foreground">Plans</p>
          <p className="text-2xl font-semibold">
            {usd(plans.reduce((sum, p) => sum + p.mrrCents, 0))}
            <span className="ml-1 text-sm font-normal text-muted-foreground">/mo</span>
          </p>
          <div className="mt-1 space-y-0.5 text-xs">
            {plans.map((plan) => (
              <div key={plan.slug} className="flex items-baseline justify-between gap-2">
                <span className="capitalize">{plan.slug}</span>
                <span className="tabular-nums text-muted-foreground">
                  {plan.organizations} orgs · {plan.users} users · {usd(plan.mrrCents)}
                </span>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
